import React, { useEffect, useRef, useState } from 'react'
import axios from 'axios'
import { useDispatch, useSelector } from 'react-redux'
import { Clock } from 'lucide-react'
import { toast } from 'react-toastify'

import { logout } from '../actions/userActions'
import { USER_LOGIN_SUCCESS } from '../constants/adminConstant'
import { Button, Modal } from '../ui'

const baseUrl = process.env.REACT_APP_API_URL

// The "continue session?" prompt opens this long before the session ends
const WARNING_BEFORE_MS = 60 * 1000

export const AUTH_NOTICE_KEY = 'authNotice'

/**
 * Portal (admin) sessions last a fixed time. A minute before the end, staff can
 * continue once for another full session; the second time it runs out they are
 * signed out without a prompt. The server enforces the same expiry.
 */
const SessionExpiryWatcher = () => {
  const dispatch = useDispatch()
  const { userInfo } = useSelector((state) => state.adminLogin || {})
  const session = userInfo?.session?.platform === 'web' ? userInfo.session : null
  const expiresAt = session?.expiresAt ? new Date(session.expiresAt).getTime() : null

  const [now, setNow] = useState(() => Date.now())
  const [extending, setExtending] = useState(false)
  const signedOutRef = useRef(false)

  useEffect(() => {
    if (!expiresAt) return undefined
    signedOutRef.current = false
    setNow(Date.now())
    const timer = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(timer)
  }, [expiresAt])

  // Another tab continued the session (or signed in again): pick up its expiry
  useEffect(() => {
    const onStorage = (event) => {
      if (event.key !== 'adminInfo' || !event.newValue) return
      try {
        const next = JSON.parse(event.newValue)
        if (next?.token && next?.session?.expiresAt !== session?.expiresAt) {
          dispatch({ type: USER_LOGIN_SUCCESS, payload: next })
        }
      } catch {
        // ignore malformed storage
      }
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [dispatch, session?.expiresAt])

  const signOut = async (notice) => {
    if (signedOutRef.current) return
    signedOutRef.current = true
    if (notice) {
      try {
        sessionStorage.setItem(AUTH_NOTICE_KEY, notice)
      } catch {
        // storage unavailable — the login page just won't show the notice
      }
    }
    await dispatch(logout())
    window.location.replace('/login')
  }

  const msLeft = expiresAt ? expiresAt - now : null

  useEffect(() => {
    if (msLeft !== null && msLeft <= 0) {
      signOut('Your session has expired. Please sign in again.')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [msLeft])

  const continueSession = async () => {
    setExtending(true)
    try {
      const { data } = await axios.post(
        `${baseUrl}/admins/sessions/extend`,
        {},
        { headers: { Authorization: `Bearer ${userInfo.token}` } },
      )
      const next = { ...userInfo, session: data.session }
      localStorage.setItem('adminInfo', JSON.stringify(next))
      dispatch({ type: USER_LOGIN_SUCCESS, payload: next })
    } catch (error) {
      toast.error(error.response?.data?.message || 'Could not continue your session')
    } finally {
      setExtending(false)
    }
  }

  const showWarning = Boolean(session?.canExtend) && msLeft !== null && msLeft > 0 && msLeft <= WARNING_BEFORE_MS
  if (!showWarning) return null

  const secondsLeft = Math.ceil(msLeft / 1000)

  return (
    <Modal
      open
      onClose={() => {}}
      closeOnBackdrop={false}
      size="sm"
      tone="warning"
      icon={Clock}
      title="Your session is about to end"
      description={`You'll be signed out in ${secondsLeft} second${secondsLeft === 1 ? '' : 's'}.`}
      footer={
        <>
          <Button variant="secondary" onClick={() => signOut()} disabled={extending}>
            Logout
          </Button>
          <Button onClick={continueSession} loading={extending} data-autofocus>
            Continue session
          </Button>
        </>
      }
    >
      <p className="text-sm text-cm-muted">
        You can continue once for another session. After that you'll be signed out automatically.
      </p>
    </Modal>
  )
}

export default SessionExpiryWatcher
